import type { Category, CategoryKind, Wallet } from '@fin/shared';

/**
 * Stroke icons on a 24px grid (Lucide-style paths). Categories and wallets get a tile with an icon:
 * a stored `icon` name wins, otherwise the name is matched against keywords.
 */
export const iconPaths: Record<string, string> = {
  tag: 'M12 2H2v10l9.3 9.3a1 1 0 0 0 1.4 0l8.6-8.6a1 1 0 0 0 0-1.4L12 2zM7 7h.01',
  cart: 'M3 4h2l2.4 11h11l2-7H6M9 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm8 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
  utensils: 'M3 2v7a3 3 0 0 0 6 0V2M6 2v20M21 15V2a5 5 0 0 0-5 5v6h5zm0 0v7',
  burger: 'M4 10h16M4 14h16M6 10c0-3 3-5 6-5s6 2 6 5M6 14c0 3 3 5 6 5s6-2 6-5',
  coffee: 'M17 8h1a4 4 0 1 1 0 8h-1M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V8zM6 2v2M10 2v2M14 2v2',
  bag: 'M6 7h12l-1 13H7L6 7zM9 7a3 3 0 0 1 6 0',
  shirt: 'M20.4 7.5L16 4l-2 2h-4L8 4 3.6 7.5 5 10l2-1v11h10V9l2 1z',
  laptop: 'M4 5h16v11H4zM2 19h20',
  repeat: 'M17 2l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 22l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3',
  car: 'M5 15l1.5-5h11L19 15M3 15h18v4H3zM7 21v-2M17 21v-2M7 10l1-3h8l1 3',
  bus: 'M5 4h14v12H5zM5 10h14M8 16v3M16 16v3M8 13h.01M16 13h.01',
  home: 'M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  phone: 'M7 2h10a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zM11 18h2',
  wifi: 'M2 9a15 15 0 0 1 20 0M5 12.5a10 10 0 0 1 14 0M8.5 16a5 5 0 0 1 7 0M12 20h.01',
  gamepad: 'M6 12h4M8 10v4M15 11h.01M18 13h.01M7 6h10a4 4 0 0 1 4 4v3a4 4 0 0 1-7 2.6L13 15h-2l-1 .6A4 4 0 0 1 3 13v-3a4 4 0 0 1 4-4z',
  gift: 'M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z',
  heart: 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l8.8 8.8 8.8-8.8a5.5 5.5 0 0 0 0-7.8z',
  hand: 'M11 11V5a2 2 0 0 1 4 0v6M15 10V4a2 2 0 0 1 4 0v8M11 10V6a2 2 0 0 0-4 0v8M7 12v-1a2 2 0 0 0-4 0v5a7 7 0 0 0 7 7h2a7 7 0 0 0 7-7v-2',
  percent: 'M19 5L5 19M7.5 9.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm9 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  health: 'M22 12h-4l-3 9L9 3l-3 9H2',
  scissors: 'M6 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM20 4L8.1 15.9M14.5 14.5L20 20M8.1 8.1L12 12',
  book: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 19.5V4.5A2.5 2.5 0 0 1 6.5 2H20v15H6.5A2.5 2.5 0 0 0 4 19.5z',
  plane: 'M2 16l20-8-6 14-3-6-6-3-5 3zM11 13l9-5',
  users: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm14 10v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8',
  trend: 'M3 17l6-6 4 4 8-8M14 7h7v7',
  briefcase: 'M2 7h20v14H2zM8 7V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3M2 13h20',
  star: 'M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8-6.2-3.2-6.2 3.2 1.2-6.8-5-4.9 6.9-1z',
  coins: 'M8 11a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM18 13a6 6 0 1 1-8.5 5.4M12 5v6M9 8h6',
  'arrow-up': 'M12 19V5M5 12l7-7 7 7',
  'arrow-down': 'M12 5v14M19 12l-7 7-7-7',
  transfer: 'M7 7h11l-3-3M17 17H6l3 3',
  wallet: 'M3 7a2 2 0 0 1 2-2h14v4M3 7v10a2 2 0 0 0 2 2h16V9H5a2 2 0 0 1-2-2zm15 7h.01',
  card: 'M3 6h18v13H3zM3 10h18',
  cash: 'M2 7h20v10H2zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 12h.01M18 12h.01',
  piggy: 'M4 10c0-3 3.6-5 8-5s8 2 8 5v5c0 3-3.6 5-8 5s-8-2-8-5zM4 12.5c0 2.5 3.6 4.5 8 4.5s8-2 8-4.5',
  bitcoin: 'M9 4v16M13 4v2M13 18v2M9 6h5a3 3 0 0 1 0 6H9m0 0h6a3 3 0 0 1 0 6H9',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
  dots: 'M12 5h.01M12 12h.01M12 19h.01',
  question: 'M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z',
  calendar: 'M3 5h18v16H3zM3 10h18M8 3v4M16 3v4',
  sparkle: 'M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8zM5 19l.7 2 2-.7-2-.7z',
  tv: 'M2 7h20v13H2zM8 3l4 4 4-4',
  music: 'M9 18V5l12-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm12-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0z',
  paw: 'M12 21c-3 0-6-2-6-5 0-2 2-4 6-4s6 2 6 4c0 3-3 5-6 5zM5 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm14 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM9 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm6 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  drop: 'M12 2.7l6.4 7.6a8 8 0 1 1-12.8 0z',
  bolt: 'M13 2L4 14h7l-1 8 9-12h-7z',
  pill: 'M10.5 20.5l10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7zM8.5 8.5l7 7',
  dumbbell: 'M6 6v12M18 6v12M3 9v6M21 9v6M6 12h12',
  baby: 'M9 12h.01M15 12h.01M10 16c.5.5 1.2.8 2 .8s1.5-.3 2-.8M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z',
  tools: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.8-3.8a6 6 0 0 1-7.9 7.9l-7 7a2.1 2.1 0 0 1-3-3l7-7a6 6 0 0 1 7.9-7.9z',
  building: 'M3 21h18M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2',
};

export type IconName = keyof typeof iconPaths;

/** keyword → icon, checked in order against the lowercase category name */
const categoryRules: [RegExp, IconName][] = [
  [/продукт|супермаркет|магазин/, 'cart'],
  [/фастфуд|бургер|пицц|шаурм|фаст/, 'burger'],
  [/кафе|кофе|ресторан|обед|ужин|завтрак|еда|бар\b/, 'coffee'],
  [/одежд|обув/, 'shirt'],
  [/электрон|техник|компьют|пк\b|гаджет/, 'laptop'],
  [/покупк|шоппинг|шопинг/, 'bag'],
  [/подписк|сервис/, 'repeat'],
  [/такси|автомоб|машин|бензин|топлив|парков|каршер/, 'car'],
  [/транспорт|метро|автобус|проезд|электричк/, 'bus'],
  [/квартплат|аренд|жкх|коммунал|жиль|ипотек|дом\b/, 'home'],
  [/телефон|связ|мобил/, 'phone'],
  [/интернет|wi-?fi/, 'wifi'],
  [/игр|steam|playstation|xbox/, 'gamepad'],
  [/подар/, 'gift'],
  [/пожертв|благотвор|донат/, 'hand'],
  [/налог|комисс|штраф|пошлин/, 'percent'],
  [/здоров|аптек|врач|лекарств|медиц|стомат/, 'health'],
  [/стрижк|парикмах|барбер|красот|салон|космет/, 'scissors'],
  [/образован|курс|учёб|учеб|книг|обучен/, 'book'],
  [/путешеств|отпуск|отел|авиа|билет|поездк/, 'plane'],
  [/родител|семь|мам|пап|перевод родным/, 'users'],
  [/инвест|инвист|акци|брокер|облигац|вклад/, 'trend'],
  [/зарплат|оклад|аванс|работ/, 'briefcase'],
  [/преми|бонус/, 'star'],
  [/кешб|кэшб|cashback|возврат/, 'coins'],
  [/дивиденд|процент|купон/, 'trend'],
  [/развлеч|кино|театр|концерт|досуг/, 'tv'],
  [/музык|spotify|яндекс музык/, 'music'],
  [/кот|кош|собак|питом|живот|корм/, 'paw'],
  [/вода|водa|doставк/, 'drop'],
  [/электрич|свет|энерг/, 'bolt'],
  [/спорт|зал|фитнес|трениров/, 'dumbbell'],
  [/дет|ребён|ребен/, 'baby'],
  [/ремонт|стро|инструм|мебел/, 'tools'],
  [/долг|кредит|займ|погашен|взыскан|рассрочк|сплит/, 'percent'],
  [/перевод/, 'transfer'],
  [/прочие поступ|поступлен|доход/, 'arrow-down'],
  [/прочие расх|расход/, 'arrow-up'],
];

const kindFallback: Record<CategoryKind, IconName> = {
  expense: 'tag', income: 'arrow-down', debt: 'percent', repayment: 'percent', loan: 'percent', debt_collection: 'percent', transfer: 'transfer',
};

export function categoryIcon(c: Pick<Category, 'name' | 'kind' | 'icon'> | { name: string; kind: CategoryKind; icon?: string }): IconName {
  if (c.icon && c.icon !== 'tag' && c.icon in iconPaths) return c.icon as IconName;
  const n = c.name.toLowerCase();
  for (const [re, icon] of categoryRules) if (re.test(n)) return icon;
  return kindFallback[c.kind] ?? 'tag';
}

const walletIcons: Record<Wallet['type'], IconName> = { bank: 'card', cash: 'cash', crypto: 'bitcoin', savings: 'piggy', investment: 'trend', other: 'wallet' };
export function walletIcon(w: Pick<Wallet, 'type' | 'icon'>): IconName {
  if (w.icon && w.icon !== 'wallet' && w.icon in iconPaths) return w.icon as IconName;
  return walletIcons[w.type] ?? 'wallet';
}

/** tile colours by kind, used when a category has no colour of its own */
export const kindTint: Record<CategoryKind, string> = {
  expense: '#F28B8B', income: '#4CB8EA', debt: '#B49CF5', repayment: '#B49CF5', loan: '#F2C14E', debt_collection: '#F2C14E', transfer: '#A1A1AA',
};

const DEFAULT_COLOR = '#8A8F98';
const palette = ['#F28B8B', '#F49D6E', '#F2C14E', '#5EE6A8', '#4FD1C5', '#7EB6FF', '#B69CFF', '#F08BC6'];

/** a category's own colour; imported categories without one get a stable colour from their name */
export function categoryColor(c: { name: string; kind: CategoryKind; color?: string }): string {
  if (c.color && c.color !== DEFAULT_COLOR) return c.color;
  if (c.kind !== 'expense') return kindTint[c.kind];
  let h = 0;
  for (const ch of c.name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return palette[h % palette.length];
}
